/**
 * Idempotent primitive attachment for a lightweight-charts series.
 *
 * Why this exists: in lightweight-charts 5.x `series.attachPrimitive(p)` never
 * checks whether `p` is already attached. Every call pushes another wrapper, so
 * the primitive is drawn once per call and its `attached()` hook (which is where
 * primitives subscribe to the chart) runs again. Clean mode re-attached every
 * overlay each time it was switched off, on top of the copy the owning effect
 * had already attached, so overlays stacked up with every click.
 *
 * The registry is installed on the series instance itself, replacing its
 * attachPrimitive/detachPrimitive, so every caller — the two dozen attach sites
 * in ChartView and library helpers such as createSeriesMarkers — goes through
 * it without having to know about it. It keeps two things apart:
 *
 *   - "wanted": what the owning code asked for (attach adds, detach removes).
 *     Owners stay in full control of their own primitive's lifetime.
 *   - "suppressed": a predicate, owned by display modes such as Clean, that can
 *     hide a wanted primitive without forgetting it is wanted.
 *
 * What is actually attached is always exactly `wanted - suppressed`, each once,
 * in the order it was first asked for. So a primitive created while it is
 * suppressed stays hidden, and lifting the suppression restores exactly what
 * the owners want — never something an owner had since removed.
 */
import type { ISeriesApi, SeriesType, Time } from 'lightweight-charts';

// Primitives are structurally typed by the library; identity is all we need.
export type AnyPrimitive = object;
export type PrimitiveSuppressor = (primitive: AnyPrimitive) => boolean;

export interface PrimitiveRegistry {
  /** Primitives the owners want attached, in first-attach order. */
  wanted(): readonly AnyPrimitive[];
  /** Primitives currently attached to the series, in attach order. */
  attached(): readonly AnyPrimitive[];
  isAttached(primitive: AnyPrimitive): boolean;
  /** Replace the suppression predicate and apply it. */
  setSuppressor(fn: PrimitiveSuppressor | null): void;
  /** Re-evaluate the current predicate (call when what it reads has changed). */
  refresh(): void;
  /** Restore the series' original methods. Attached primitives stay attached. */
  uninstall(): void;
}

const REGISTRY = Symbol.for('utt.primitiveRegistry');

type Patchable = Record<string | symbol, unknown>;

/** The registry installed on a series, if any. */
export function getPrimitiveRegistry(series: unknown): PrimitiveRegistry | undefined {
  if (!series) return undefined;
  return (series as Patchable)[REGISTRY] as PrimitiveRegistry | undefined;
}

/**
 * Install (or return the already-installed) registry on `series`. Must run
 * before anything else attaches to the series, or those earlier attachments are
 * invisible to it.
 */
export function installPrimitiveRegistry<T extends SeriesType>(
  series: ISeriesApi<T, Time>,
  suppressor: PrimitiveSuppressor | null = null,
): PrimitiveRegistry {
  const existing = getPrimitiveRegistry(series);
  if (existing) {
    existing.setSuppressor(suppressor);
    return existing;
  }

  const s = series as unknown as Patchable;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rawAttach = (series.attachPrimitive as any).bind(series) as (p: AnyPrimitive) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rawDetach = (series.detachPrimitive as any).bind(series) as (p: AnyPrimitive) => void;
  const wanted: AnyPrimitive[] = [];
  // Mirrors the library's own list, in its order.
  const attached: AnyPrimitive[] = [];
  let suppress: PrimitiveSuppressor | null = suppressor;

  const isSuppressed = (p: AnyPrimitive) => {
    if (!suppress) return false;
    try {
      return suppress(p);
    } catch {
      // A broken predicate must not take overlays with it: show it.
      return false;
    }
  };

  const doAttach = (p: AnyPrimitive) => {
    // Recorded first: the library pushes its wrapper before calling the
    // primitive's attached() hook, so if that hook throws the wrapper is still
    // on the series and must stay detachable.
    attached.push(p);
    rawAttach(p);
  };
  const doDetach = (p: AnyPrimitive) => {
    const i = attached.indexOf(p);
    if (i === -1) return;
    attached.splice(i, 1);
    try {
      rawDetach(p);
    } catch {
      /* series already removed */
    }
  };

  /**
   * Bring the series to exactly `wanted - suppressed`, preserving wanted order.
   * A primitive coming back from suppression would otherwise land on top of
   * everything attached after it, so those are detached and re-attached
   * behind it to keep the z-order the owners originally produced.
   */
  const sync = () => {
    const target = wanted.filter((p) => !isSuppressed(p));
    for (const p of [...attached]) {
      if (!target.includes(p)) doDetach(p);
    }
    // `attached` is now a subsequence of `target`; find the first divergence.
    let i = 0;
    for (; i < attached.length; i++) {
      if (attached[i] !== target[i]) break;
    }
    if (i === attached.length && attached.length === target.length) return;
    for (const p of attached.slice(i)) doDetach(p);
    const toAttach = target.slice(i);
    for (const p of toAttach) doAttach(p);
    // The library repaints on detach but not on attach; an attach-only restore
    // (nothing after it to detach) would otherwise wait for the next tick.
    if (toAttach.length) {
      try {
        (series as unknown as { applyOptions?: (o: object) => void }).applyOptions?.({});
      } catch {
        /* series already removed */
      }
    }
  };

  const registry: PrimitiveRegistry = {
    wanted: () => wanted.slice(),
    attached: () => attached.slice(),
    isAttached: (p) => attached.includes(p),
    setSuppressor: (fn) => {
      suppress = fn;
      sync();
    },
    refresh: sync,
    uninstall: () => {
      s.attachPrimitive = rawAttach;
      s.detachPrimitive = rawDetach;
      delete s[REGISTRY];
    },
  };

  s.attachPrimitive = (p: AnyPrimitive) => {
    if (!p || wanted.includes(p)) return; // idempotent: a second attach is a no-op
    wanted.push(p);
    if (!isSuppressed(p)) doAttach(p);
  };
  s.detachPrimitive = (p: AnyPrimitive) => {
    const i = wanted.indexOf(p);
    if (i !== -1) wanted.splice(i, 1);
    doDetach(p);
  };
  Object.defineProperty(series, REGISTRY, { value: registry, configurable: true });
  return registry;
}
